import { z } from 'zod';

import { parseScriptOutputs, serializeScriptOutputs, ScriptOutputSchema, type ScriptOutput } from './script-step';

/**
 * Schemas for the `run-js` flow step type — a sandboxed inline JavaScript
 * snippet that runs IN THE BROWSER PAGE CONTEXT via Playwright's
 * `page.evaluate()`. Distinct from the existing `script` step (see
 * `./script-step.ts`), which is a server-side data-shaping subprocess
 * (TS / Python / Java) with no DOM access.
 *
 * See `docs/handbook/features/pending/runjs-step.md` for the design doc
 * and the comparison table — these two steps compose (a flow can use
 * `runJS` to read DOM state into a flow variable, then `script` to
 * transform it server-side, then `assertion` to check the result).
 *
 * Wire shape:
 *   - `code`     — user JS, capped at `MAX_RUNJS_CODE_BYTES` (32 KB).
 *   - `timeout`  — optional ms string; clamped to `[1, MAX_RUNJS_TIMEOUT_MS]`.
 *   - `outputs`  — JSON-encoded `ScriptOutput[]` (REUSES the existing
 *                  script-step output declaration shape so the editor
 *                  can share its `<OutputsEditor>` component and the
 *                  runner-→-API output-variable wire shape stays
 *                  identical to the existing `ScriptOutputUpdate`).
 *   - `network`  — `'inherit' | 'blocked'`; default `inherit` so
 *                  `fetch` against the page origin works under CSP.
 *                  Operators who want "no outbound traffic from a
 *                  `runJS` step at all" flip `defaultNetwork` to
 *                  `blocked` in admin config (coarse — finer
 *                  allowlists are a v2 ask).
 *   - `description` — optional one-liner that surfaces in the run-report.
 *
 * Security posture is layered (master kill switch in admin config,
 * plan-tier gate, bytes cap, time cap, banned-pattern lint over the
 * code body, no vault access from the browser context). The browser
 * sandbox is doing the bulk of the work; this contract slice is the
 * pre-flight filter.
 */

export const RunJsNetworkModeSchema = z.enum(['inherit', 'blocked']);
export type RunJsNetworkMode = z.infer<typeof RunJsNetworkModeSchema>;

export const DEFAULT_RUNJS_TIMEOUT_MS = 5_000;
export const MAX_RUNJS_TIMEOUT_MS = 60_000;
export const DEFAULT_RUNJS_CODE_BYTES = 32_768;
export const MAX_RUNJS_CODE_BYTES = 131_072;

/**
 * Stored shape of one `run-js` step's `flow_steps.fields` JSONB. The
 * editor model is a flat `Record<string, string>` (see flow-builder's
 * `FlowStep.fields`), so the structured `outputs` declaration is JSON-
 * encoded into the single `outputs` string — exactly the trick the
 * existing `script` step uses. `parseRunJsOutputs` /
 * `serializeRunJsOutputs` delegate to the script-step helpers so the
 * two stay byte-identical on the wire.
 */
export const RunJsStepFieldsSchema = z.object({
  code: z.string().max(MAX_RUNJS_CODE_BYTES),
  timeout: z.string().regex(/^\d+$/).optional(),
  outputs: z.string().default('[]'),
  network: RunJsNetworkModeSchema.default('inherit'),
  description: z.string().max(200).optional(),
});
export type RunJsStepFields = z.infer<typeof RunJsStepFieldsSchema>;

/**
 * Banned substrings the API-side validator refuses to save. The real
 * defence is the browser sandbox; this catches the lazy attack (`eval(`
 * / `new Function(` / `Function(` round-trip exposure) and signals
 * organisational intent. Whitespace-tolerant — every variant matches
 * the same physical attack surface.
 *
 * The list is intentionally short and explicit. We do NOT lint for
 * `fetch(` (legitimate page-origin requests), `XMLHttpRequest` (same),
 * `import(` (dynamic import is browser-native), or `document.cookie`
 * (legitimate when reading session state). Those decisions belong on
 * the CSP / cookie-policy / origin layer of the customer's app, not in
 * an editor lint.
 */
export const RUNJS_BANNED_PATTERNS = [/\beval\s*\(/, /\bnew\s+Function\s*\(/, /(?:^|[^.\w])Function\s*\(/] as const;

/**
 * Returns the first matching banned pattern (or `null` for clean code).
 * Pure function — used by both the API-side validator and the editor's
 * inline-lint indicator so the user gets the same answer everywhere.
 */
export function findBannedRunJsPattern(code: string): RegExp | null {
  for (const pattern of RUNJS_BANNED_PATTERNS) {
    if (pattern.test(code)) return pattern;
  }
  return null;
}

export function parseRunJsOutputs(raw: string | undefined | null): ScriptOutput[] {
  return parseScriptOutputs(raw);
}

export function serializeRunJsOutputs(outputs: ScriptOutput[]): string {
  return serializeScriptOutputs(outputs);
}

/**
 * Wire shape returned by `POST /api/runjs/preview`. The endpoint
 * validates the body without executing — actual execution happens on
 * the runner against a real browser page (the API server has no
 * browser to drive). The preview response is the canonical "your code
 * passes the pre-flight filter; the runner will accept it" answer.
 */
export const RunJsPreviewRequestSchema = z.object({
  code: z.string().max(MAX_RUNJS_CODE_BYTES),
  timeoutMs: z.number().int().min(1).max(MAX_RUNJS_TIMEOUT_MS).optional(),
  outputs: z.array(ScriptOutputSchema).max(32).optional(),
  network: RunJsNetworkModeSchema.optional(),
});
export type RunJsPreviewRequest = z.infer<typeof RunJsPreviewRequestSchema>;

export const RunJsPreviewResponseSchema = z.object({
  ok: z.literal(true),
  codeBytes: z.number().int().nonnegative(),
  codeHash: z.string().regex(/^[0-9a-f]{32}$/),
  timeoutMs: z.number().int().min(1),
  network: RunJsNetworkModeSchema,
  outputsCount: z.number().int().nonnegative(),
});
export type RunJsPreviewResponse = z.infer<typeof RunJsPreviewResponseSchema>;

/**
 * Wraps the user's code in the `async ()` IIFE the runner evaluates.
 * Pure — extracted so the unit tests can assert on the wrapping shape
 * without needing a real Playwright page. The wrapper is invariant
 * across runs (so a stable code hash means a stable wrapped script
 * too); the audit log captures the hash of the USER code only.
 *
 * The wrapped script:
 *   1. JSON-decodes the flow-variable inputs into `__inputs`.
 *   2. Sets up `__outputs` + `__logs` collectors.
 *   3. Constructs the in-page `rynora` runtime object.
 *   4. `await`s the user code wrapped in an async IIFE so the user
 *      can write either a single expression (the value bubbles out as
 *      the return value) or a multi-statement body with an explicit
 *      `return` — both shapes work without the user having to know
 *      the wrapping detail.
 *   5. Returns the envelope `{ returnValue, outputs, logs }` so the
 *      runner can split it into the step result + variable bus
 *      writes + log entries without a second `page.evaluate` round
 *      trip.
 *
 * Errors from inside the user code are rethrown as a normal
 * `Error` and surface to the runner with the page-side stack. The
 * runner's caller normalises that through the existing
 * `extractTopStackFrames` helper from `error-fingerprint.ts` so the
 * test-observability tier picks up `runJS` failures with the same
 * fingerprint pipeline as every other step.
 */
export function wrapRunJsCode(userCode: string): string {
  return `(async () => {
  const __inputs = window.__rynoraInputs__ || {};
  const __outputs = {};
  const __logs = [];
  const rynora = {
    input: (k) => __inputs[k],
    output: (k, v) => { if (typeof k === 'string' && k.length > 0) { __outputs[k] = v; } },
    log: (msg, level) => { __logs.push({ msg: String(msg), level: level === 'warn' || level === 'error' ? level : 'info', ts: Date.now() }); },
    fail: (msg) => { const e = new Error(String(msg)); e.__runjsFail = true; throw e; },
    element: (sel) => document.querySelector(sel),
    elements: (sel) => Array.from(document.querySelectorAll(sel)),
    expectVisible: (sel) => {
      const el = document.querySelector(sel);
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return false;
      const style = window.getComputedStyle(el);
      return style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0';
    },
    expectAttached: (sel) => !!document.querySelector(sel),
  };
  const __returnValue = await (async () => {
${userCode}
  })();
  return { returnValue: __returnValue, outputs: __outputs, logs: __logs };
})();`;
}
